import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@components/ui/dialog';
import { getAllServiceProspects, SERVICE_TYPES } from '@generated/server/services';

type Row = Awaited<ReturnType<typeof getAllServiceProspects>>['items'][number];
type SType = (typeof SERVICE_TYPES)[number];
export type ServiceEdit = { name: string; contactName: string; address: string; phone: string; email: string; website: string; serviceType: SType; notes: string };

const field = 'mt-1 w-full min-h-11 border-2 border-foreground bg-background px-3 py-2 text-sm';

/** Edit form for one Neighborhood Services prospect. */
export function ServiceEditDialog({ row, onClose, onSave }: { row: Row; onClose: () => void; onSave: (v: ServiceEdit) => Promise<void> }) {
  const st = SERVICE_TYPES.find((s) => s === row.serviceType) ?? 'Other';
  const [v, setV] = useState<ServiceEdit>({ name: row.name, contactName: row.contactName ?? '', address: row.address ?? '', phone: row.phone ?? '', email: row.email?.email ?? '', website: row.website ?? '', serviceType: st, notes: row.notes ?? '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const set = (k: keyof ServiceEdit) => (e: { target: { value: string } }) => setV({ ...v, [k]: e.target.value });

  async function save() {
    if (!v.name.trim()) { setErr('Business name is required.'); return; }
    setBusy(true); setErr('');
    try { await onSave(v); onClose(); } catch (e) { console.error(e); setErr('Could not save. Please try again.'); } finally { setBusy(false); }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-none border-2 border-foreground sm:max-w-lg">
        <DialogHeader><DialogTitle className="font-[family-name:var(--font-heading)] text-xl">Edit prospect</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-sm font-semibold sm:col-span-2">Business name<input className={field} value={v.name} onChange={set('name')} /></label>
          <label className="text-sm font-semibold">Contact name<input className={field} value={v.contactName} onChange={set('contactName')} /></label>
          <label className="text-sm font-semibold">Phone<input className={field} value={v.phone} onChange={set('phone')} /></label>
          <label className="text-sm font-semibold">Email<input type="email" className={field} value={v.email} onChange={set('email')} /></label>
          <label className="text-sm font-semibold">Website<input className={field} value={v.website} onChange={set('website')} /></label>
          <label className="text-sm font-semibold sm:col-span-2">Mailing address<textarea rows={2} className={field} value={v.address} onChange={set('address')} /></label>
          <label className="text-sm font-semibold sm:col-span-2">Service type
            <select className={field} value={v.serviceType} onChange={set('serviceType')}>{SERVICE_TYPES.map((s) => <option key={s}>{s}</option>)}</select>
          </label>
          <label className="text-sm font-semibold sm:col-span-2">Notes<textarea rows={4} className={field} value={v.notes} onChange={set('notes')} /></label>
        </div>
        {err && <p className="text-sm text-destructive">{err}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="min-h-11 border-2 border-foreground bg-card px-4 text-sm font-semibold">Cancel</button>
          <button type="button" onClick={save} disabled={busy} className="inline-flex min-h-11 items-center gap-2 border-2 border-foreground bg-primary px-4 text-sm font-semibold text-primary-foreground disabled:opacity-50">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Save</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
